import { isNil } from '@packages/helpers/core/common';
import { NumberShim } from '@packages/helpers/core/shims/number-shim';
import { ObjectShim } from '@packages/helpers/core/shims/object-shim';
import { ConfigService } from '../../services/config-service';
import { formatValueToPercent } from '../formatters';
import { toMoneyStringWithComma } from '../formatters/money-format';
import { getS3EntityByAffiliate } from '../layout';
import { getDateFromString } from '../time-format';
import { DIFFICULTIES_MAPPERS_CONFIG, PENSION_COMPANY_SIZES, PENSION_DIFFICULTIES } from './constants';

export const formatDate = (value, format) => {
  const input = value ? 'MM/YYYY' : void 0;

  return getDateFromString(value, {
    input,
    output: format
  });
};

const formatDiffToPercent = (value = 0) => formatValueToPercent(value, 4);

const getTimelineLength = ({ start, finish }) => finish.diff(start, 'y', true);

export const getCareer = ({ firstJobAt, dateOfBirth }) => {
  const birthday = formatDate(dateOfBirth);
  const start = formatDate(firstJobAt);
  const finish = formatDate();
  const length = getTimelineLength({ start, finish });

  return {
    birthday,
    start,
    finish,
    length
  };
};

export const getTimelineItems =
  styles =>
  ({ start: startOfCareer, finish: endOfCareer, length, items = [] }) => {
    let table = [];

    return items.reduce((acc, company) => {
      const { jobStartedAt, jobFinishedAt, hasDetails } = company;

      const start = formatDate(jobStartedAt);
      const end = formatDate(jobFinishedAt);

      const from = start.diff(startOfCareer, 'y', true);
      const to = endOfCareer.diff(end, 'y', true);

      const style = {
        left: NumberShim.percentOf(from, length, true, formatDiffToPercent),
        right: NumberShim.percentOf(to, length, true, formatDiffToPercent)
      };

      const rows = [].concat(acc);

      for (let index = 0; index < rows.length; index++) {
        const row = rows[index];
        const rowLastItemEndDate = table[index];

        if (rowLastItemEndDate.isSameOrBefore(start)) {
          table[index] = end;
          row.push({ style, className: styles.bar, 'aria-disabled': !hasDetails });

          return rows;
        }
      }

      table.push(end);
      rows.push([{ style, className: styles.bar, 'aria-disabled': !hasDetails }]);

      return rows;
    }, []);
  };

export const getTimelinePeriods = ({ start, finish, length }) => {
  const startPoint = start.year();

  let marks = [],
    cursor = finish.year();

  if (cursor % 10) {
    cursor -= cursor % 10;
  }

  do {
    marks.unshift(cursor);

    cursor -= 10;
  } while (cursor >= startPoint);

  // each mark should be a fucntion.
  // because of ChartGrid have to use
  // custom offseting for each line instead
  // of linear distribution
  return marks.map(mark => () => ({
    mark,
    offset: NumberShim.percentOf(mark - startPoint, length)
  }));
};

export const getSortedCompanies = items => {
  return ObjectShim.entries(items)
    .map(([slug, company]) => ({ ...company, slug }))
    .sort(({ jobStartedAt: left }, { jobStartedAt: right }) => {
      return formatDate(left).valueOf() - formatDate(right).valueOf();
    });
};

export const buildCompanyCard = ({
  slug,
  hasDetails,
  companyName,
  companySize,
  jobStartedAt,
  jobFinishedAt,
  pensionStatus
}) => {
  const start = jobStartedAt && formatDate(jobStartedAt);
  const finish = jobFinishedAt && formatDate(jobFinishedAt);

  return {
    slug,
    hasDetails,
    pensionStatus,
    complexity: hasDetails ? null : companySize,
    title: companyName,
    subTitle: [start, finish]
      .filter(date => date?.isValid())
      .map(date => date.year())
      .join(' - ')
  };
};

const COMPANY_ATTRIBUTE_FORMATTER = {
  currency: toMoneyStringWithComma,
  date: formatDate
};

export const buildCompanyAttributes = attributes => {
  const { keys } = ConfigService.get('PENSION_FINDER.JOB_CARD.attributes', {});

  return keys.reduce((acc, { key, label, format, transform }) => {
    const value = ObjectShim.getNested(attributes, key);

    if (!isNil(value)) {
      acc.push({
        name: label,
        value: format in COMPANY_ATTRIBUTE_FORMATTER ? COMPANY_ATTRIBUTE_FORMATTER[format](value, transform) : value
      });
    }

    return acc;
  }, []);
};

const mapServices = (services = []) => {
  const { SERVICE_SEPARATOR, MATCH_LOWER_SERVICE, MATCH_UPPER_SERVICE } = DIFFICULTIES_MAPPERS_CONFIG;

  return services.map(service => {
    if (service.startsWith(MATCH_LOWER_SERVICE)) {
      return [-Infinity, parseInt(service.slice(MATCH_LOWER_SERVICE.length).trim())];
    }

    if (service.endsWith(MATCH_UPPER_SERVICE)) {
      return [parseInt(service), +Infinity];
    }

    return service.split(SERVICE_SEPARATOR).map(date => parseInt(date));
  });
};

const mapPeriods = (periods = []) => {
  const { PERIOD_SEPARATOR, MATCH_LOWER_PERIOD, MATCH_UPPER_PERIOD } = DIFFICULTIES_MAPPERS_CONFIG;

  return periods.map(period => {
    if (period.startsWith(MATCH_LOWER_PERIOD)) {
      return [null, `01/${parseInt(period.slice(MATCH_LOWER_PERIOD.length).trim())}`];
    }

    if (period.endsWith(MATCH_UPPER_PERIOD)) {
      return [`01/${parseInt(period)}`, null];
    }

    return period.split(PERIOD_SEPARATOR).map(date => `01/${date}`);
  });
};

export const mapComplexityToDifficultyLevel = ({ levels, services, periods }, { start, finish, type }) => {
  const length = getTimelineLength({ start, finish });

  const serviceIndex = mapServices(services).findIndex(([from, before]) => {
    return length >= from && length < before;
  });

  const timeIndex = mapPeriods(periods).findIndex(([from, before]) => {
    return start.isBetween(formatDate(from), formatDate(before));
  });

  const companyKey = PENSION_COMPANY_SIZES[type];

  if (serviceIndex > -1 && timeIndex > -1 && companyKey) {
    return levels[companyKey][timeIndex][serviceIndex];
  }

  return PENSION_DIFFICULTIES.HIGH;
};

export const getJobCardFallbackImage = () => {
  const { fallbackImage } = ConfigService.get('PENSION_FINDER.JOB_CARD', {});

  return { src: getS3EntityByAffiliate(fallbackImage) };
};

export const getAvatarInitials = entityName => {
  const regExp = new RegExp(/\b([a-zA-Z0-9])/g);
  const letters = entityName.match(regExp);

  let result = '-';

  if (letters.length > 1) {
    result = letters[0] + letters[letters.length - 1];
  }

  if (letters.length === 1) {
    result = letters[0];
  }

  return result.toUpperCase();
};

// If startDate any of job will be earlier than firstJobDate it will replace it as a first timeline entry point.
export const defineFirstTimelineJobDate = ({ items, firstJobAt }) => {
  return items.reduce((prev, { jobStartedAt }) => {
    const diffInYears = formatDate(prev).diff(formatDate(jobStartedAt), 'y');
    prev = diffInYears > 0 ? jobStartedAt : prev;
    return prev;
  }, firstJobAt);
};

export const formatCurrency = (amount, currency) =>
  currency || '£' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const checkPensionsReadiness = companies => companies.some(({ pensionDetailsReady }) => !pensionDetailsReady);

export const checkTimelineGap = ({ companies, firstJobAt, monthGap }) => {
  const formattedFirstJobStarted = formatDate(firstJobAt);
  const formattedFirstCompanyFinished = formatDate(companies[0].jobFinishedAt);

  let hasGap = formattedFirstCompanyFinished.diff(formattedFirstJobStarted, 'M') >= 5;
  for (let i = 0; i < companies.length - 1; i++) {
    const curr = companies[i];
    const next = companies[i + 1];

    const formattedCompanyStarted = formatDate(curr.jobFinishedAt);
    const formattedCompanyFinished = formatDate(next.jobStartedAt) || formattedFirstJobStarted;

    if (!formattedCompanyStarted) {
      continue;
    }

    hasGap = formattedCompanyFinished.diff(formattedCompanyStarted, 'M') >= monthGap;

    if (hasGap) {
      break;
    }
  }

  return hasGap;
};

export const definePensionStatus = (initialItems, difficulties) =>
  Object.keys(initialItems).reduce((acc, key) => {
    const item = initialItems[key];
    const pensionStatus = item.hasDetails
      ? PENSION_DIFFICULTIES.FOUND
      : mapComplexityToDifficultyLevel(difficulties, {
          start: formatDate(item.jobStartedAt),
          finish: formatDate(item.jobFinishedAt),
          type: item.companySize
        });
    acc[key] = { ...item, pensionStatus };
    return acc;
  }, {});
